<template>
  <div>
    <div
      v-if="!hideCalendar"
      class="inline-calendar-area"
    >
      <!-- Lesson calendar -->
      <div ref="calendar-area" :style="`min-height: ${calendarAreaMinHeight}`">
        <div v-show="!loadingCalendar">
          <b-datepicker
            inline
            :first-day-of-week="1"
            v-model="date"
            v-on:change-month="calendarMonthChanges"
            v-on:change-year="calendarYearChanges"
            :events="lessonsDates"
            :min-date="minDate()"
            :max-date="maxDate()"
            indicators="bars"
            :nearby-month-days="false"
          />
        </div>
      </div>

      <!-- Loading lesson calendar -->
      <div v-if="loadingCalendar">
        <spinner />
      </div>
    </div>

    <div v-if="!loadingDay">
      <div v-if="groupPlannedLessons().length > 0">
        <!-- Group lesson card -->
        <div
          class="card planned-lesson-card mt-3"
          v-for="(groupLesson, index) in groupPlannedLessons()"
          :key="`group-lesson-${index}`"
        >
          <div class="card-content">
            <!-- Lesson title -->
            <p class="planned-lesson-title">
              {{ groupLesson.lesson.name }}
              <strong v-if="showDuration(groupLesson.lesson)" class="lesson-duration">
                <b-icon size="is-small" icon="clock-outline" class="mr-0" />
                {{ groupLesson.lesson.duration }}
              </strong>
              <strong v-if="groupLesson.lesson.is_free">
                {{ $t("is_free") }}
              </strong>
              <strong v-else-if="!slotable">
                {{ groupLesson.lesson.amount }}
              </strong>
            </p>

            <p class="mt-1 mb-2" v-if="groupLesson.lesson.description">
              {{ groupLesson.lesson.description }}
            </p>

            <div class="columns is-mobile pl-3">
              <div
                v-if="timeFormat(groupLesson.lesson)"
                class="column max-width-column text-grey pb-0 pl-0"
              >
                <small>
                  {{ $t("start") }}
                </small>
              </div>
              <div
                v-if="showRemainingPlace(groupLesson.lesson)"
                class="column max-width-column text-grey has-text-centered-desktop-only pb-0 pl-0 pr-0"
              >
                <small>
                  {{ $t("availability") }}
                </small>
              </div>
              <div class="column pb-0"></div>
            </div>

            <planned-lesson-row
              v-for="plannedLesson in groupLesson.plannedLessons"
              :key="plannedLesson.id"
              :planned-lesson="plannedLesson"
              :lesson="groupLesson.lesson"
              :slotable="slotable"
              :max-slotable-places="maxSlotablePlaces"
              :slotable-callback="slotableCallback"
              :show-remaining-place="showRemainingPlace(groupLesson.lesson)"
              :time-slot-format="timeFormat(groupLesson.lesson)"
              :get-daily-lesson="dailyLesson"
              :direct-to-card="directToCard"
            />
          </div>
        </div>
      </div>
      <div v-else-if="isFullDate(date)">
        <p class="text-danger text-center mt-4">
          {{ $t("allIsFull", { date: humanizeDate(date) }) }}
        </p>
      </div>
      <div v-else>
        <p class="text-grey text-center mt-4">
          {{ $t("noCoursesAvailableOn", { date: humanizeDate(date) }) }}
        </p>
      </div>

      <!-- Next date btn if have next date -->
      <div v-if="nextDate && !hideCalendar" class="text-center">
        <b-button
          class="mt-4"
          size="is-small"
          type="is-light"
          rounded
          icon-left="calendar-arrow-right"
          @click="date = nextDate"
        >
          {{ $t("nextDateAt", { date: humanizeDate(nextDate) }) }}
        </b-button>
      </div>

      <!-- Next month if have no next date -->
      <div v-if="!nextDate && !hideCalendar" class="text-center">
        <b-button
          class="mt-4"
          size="is-small"
          type="is-light"
          rounded
          icon-left="calendar-arrow-right"
          @click="date = nextMonth"
        >
          {{ $t("nextMonth", { month: humanizeDate(nextMonth, "MMMM") }) }}
        </b-button>
      </div>
    </div>
    <div v-if="loadingDay">
      <spinner :show-text="false" />
    </div>
  </div>
</template>

<script>
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { DateHelpers } from "@/mixins/DateHelpers";
import { CustomisationMixin } from "@/mixins/CustomisationMixin";
import cug from "../services/cug";
import Spinner from "./shared/Spinner";
import VueI18n from "vue-i18n";
import moment from "../services/momentjs";
import PlannedLessonRow from "./partial/PlannedLessonRow";

export default {
  name: "LessonCalendar",
  components: { Spinner, PlannedLessonRow },
  mixins: [LocaleMixin, HelpersMixin, DateHelpers, CustomisationMixin],
  props: {
    lessonIds: {
      type: String,
      require: false,
      default: "*"
    },
    slotable: {
      type: Boolean,
      default: false
    },
    maxSlotablePlaces: {
      type: [Number, String],
      default: null
    },
    slotableCallback: {
      type: Function,
      default: null
    },
    openAt: {
      type: String,
      default: null
    },
    hideCalendar: {
      type: Boolean,
      default: false
    },
    directToCard: {
      type: Boolean,
      required: false
    },

    // TODO : Delete after widget-options depreciation
    widgetOptions: {
      type: String,
      required: false,
      default: "showRemainingPlace=true;timeSlotFormat=HH[h]mm;showDuration=true"
    }
  },

  data() {
    return {
      date: null,
      calendarMonth: null,
      calendarYear: null,
      calendarGettingMonths: [],
      calendarAreaMinHeight: "0px",

      lessonsDates: [],

      lessons: [],
      plannedLessons: [],
      fullLessons: [],

      loadingCalendar: true,
      loadingDay: true
    };
  },

  computed: {
    calendarFocusDate() {
      const month = ("0" + this.calendarMonth).slice(-2);
      return `${this.calendarYear}-${month}-01`;
    },

    nextDate() {
      for (const lessonsDate of this.lessonsDates) {
        if (
          moment(lessonsDate.date).isAfter(moment(this.date).hour(23).minute(59)) &&
          lessonsDate.type === "lesson-color"
        ) {
          return lessonsDate.date;
        }
      }
      return null;
    },

    nextMonth() {
      return moment(this.calendarFocusDate)
        .add(1, "month")
        .toDate();
    }
  },

  watch: {
    date() {
      this.loadingDay = true;
      setTimeout(() => {
        this.dailyLesson();
      }, 300);
    },

    calendarFocusDate() {
      this.lessonsInMonth(this.calendarFocusDate);
    }
  },

  created() {
    let urlParams = new URLSearchParams(window.location.search);
    const paramDate = this.openAt || urlParams.get("date");

    this.date = paramDate ? new Date(paramDate) : new Date();
    this.calendarMonth = this.date.getMonth() + 1;
    this.calendarYear = this.date.getFullYear();
  },

  beforeMount() {
    this.lessonsInMonth(this.shortDate(new Date().setDate(1))).then(() => {
      this.dailyLesson();
    });
  },

  methods: {
    calendarMonthChanges(month) {
      this.calendarMonth = month + 1;
    },

    calendarYearChanges(year) {
      this.calendarYear = year;
    },

    lessonsIdsArray() {
      if (this.lessonIds === "*") {
        return [];
      } else {
        return this.lessonIds.replace(/\s/g, "").split(",");
      }
    },

    minDate() {
      return moment()
        .subtract(1, "day")
        .toDate();
    },

    maxDate() {
      return moment()
        .add(1, "year")
        .endOf("month")
        .toDate();
    },

    lessonsInMonth(date) {
      if (this.calendarGettingMonths.includes(date)) {
        return false;
      } else {
        this.calendarGettingMonths.push(date);
      }

      this.loadingCalendar = true;
      return new Promise((resolve) => {
        cug
          .monthlyLessons({
            month: date,
            lessons_ids: this.lessonsIdsArray()
          })
          .then(resp => {
            this.lessons = resp.data;
            for (let lesson of this.lessons) {
              if (moment(lesson.date).isBefore(moment().subtract(1, "day"))) {
                continue;
              }

              this.lessonsDates.push({
                date: new Date(lesson.date),
                type: "lesson-color"
              });
            }

            // Sort lesson by date
            this.lessonsDates.sort((a, b) => {
              const x = a.date;
              const y = b.date;
              return x < y ? -1 : x > y ? 1 : 0;
            });

            // get lesson without remaining places
            this.lessonFull(date);

            resolve(resp);
          })
          .finally(() => {
            this.loadingCalendar = false;
            setTimeout(() => {
              this.calendarAreaMinHeight = `${this.$refs["calendar-area"].offsetHeight}px`;
            }, 500);
          });
      });
    },

    lessonFull(date) {
      cug
        .monthlyFullLessons({
          month: date,
          lessons_ids: this.lessonsIdsArray()
        })
        .then(resp => {
          for (let dateFull of resp.data) {
            if (moment(dateFull).isBefore(moment().subtract(1, "day"))) {
              continue;
            }
            this.lessonsDates.push({
              date: new Date(dateFull),
              type: "lesson-full-color"
            });
          }
        });
    },

    isFullDate(date) {
      for (const fullDate of this.lessonsDates) {
        if (
          fullDate.date.toDateString() === date.toDateString() &&
          fullDate.type === "lesson-full-color"
        ) {
          return true;
        }
      }
      return false;
    },

    dailyLesson() {
      cug
        .dailyLessons({
          day: this.shortDate(this.date),
          lessons_ids: this.lessonsIdsArray()
        })
        .then(rep => {
          this.plannedLessons = rep.data;
          this.groupPlannedLessons();
        })
        .finally(() => {
          this.loadingDay = false;
        });
    },

    groupPlannedLessons() {
      const group = {};
      for (const plannedLesson of this.plannedLessons) {
        const key = `${plannedLesson.lesson_id}${plannedLesson.duration}`;
        group[key] = (group[key] || {
          lesson: {
            name: plannedLesson.name,
            description: plannedLesson.description,
            duration: this.minutesDurationToHours(plannedLesson.duration),
            amount: `${plannedLesson.withtax_price.amount / 100}${plannedLesson.withtax_price.currency.symbol}`,
            online_sales_parameters: plannedLesson.online_sales_parameters,
            is_free: plannedLesson.is_free,
            tenant: plannedLesson.tenant_name
          },
          plannedLessons: []
        });
        group[key]["plannedLessons"].push(plannedLesson);
      }
      return Object.values(group);
    },

    showDuration(lesson) {
      if (lesson.online_sales_parameters?.hide_duration) {
        return !lesson.online_sales_parameters.hide_duration;
      } else if (this.customisation.showDuration) {
        return this.customisation.showDuration;
      } else {
        return true;
      }
    },

    showRemainingPlace(lesson) {
      if (lesson.online_sales_parameters?.hide_remaining_place) {
        return !lesson.online_sales_parameters.hide_remaining_place;
      } else if (this.customisation.showRemainingPlace) {
        return this.customisation.showRemainingPlace;
      } else {
        return true;
      }
    },

    timeFormat(lesson) {
      if (lesson.online_sales_parameters?.time_format) {
        return lesson.online_sales_parameters.time_format;
      } else if (this.customisation.timeSlotFormat) {
        return this.customisation.timeSlotFormat;
      } else {
        return "HH[h]mm";
      }
    }
  },

  // Translate component
  mounted() {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        start: "Start",
        availability: "Availability",
        noCoursesAvailableOn: "No courses available on {date}",
        nextDateAt: "Next date at {date}",
        nextMonth: "See dates in {month}",
        noReservationAvailable: "We are sorry,<br>there are currently no reservations available.",
        allIsFull: "All our courses are full on {date}",
        is_free: "Free"
      },
      fr: {
        start: "Début",
        availability: "Places restantes",
        noCoursesAvailableOn: "Pas de cours disponible le {date}",
        nextDateAt: "Date suivante le {date}",
        nextMonth: "Voir les dates en {month}",
        noReservationAvailable: "Nous sommes désolé,<br>il n'y a aucune réservation disponible actuellement.",
        allIsFull: "Tous nos cours sont complets le {date}",
        is_free: "Gratuit"
      }
    }
  })
};
</script>

<style lang="scss" src="../assets/styles/buefy/base.scss"></style>
<style lang="scss">
.lesson-color {
  background-color: #43a047;
}

.lesson-full-color {
  background-color: #e23f3f;
}

.inline-calendar-area {
  .dropdown-menu {
    position: relative;
  }
}

.planned-lesson-card {
  .planned-lesson-title {
    text-transform: capitalize;
    margin-bottom: 0.5em;
    .lesson-duration {
      margin-left: 0.5em;
      margin-right: 0.5em;
    }
  }
  .card-content {
    padding: 0.7rem;
    .max-width-column {
      max-width: 110px;
    }
  }
}
</style>
